import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiLoader, FiCheck, FiAlertCircle, FiClock } from "react-icons/fi";
import { getOrderStatus } from "../api/checkout";
import { useSocket } from "../context/SocketContext";

const SOCKET_KEEPZ_PAYMENT = "keepz_payment";

/** Backoff between polls, in seconds. The last value repeats until the ceiling. */
const POLL_STEPS = [1, 2, 3, 5, 5, 8];
/** After this we stop polling and tell the truth: it's still processing. */
const CEILING_MS = 90 * 1000;

/**
 * Where Keepz sends the buyer back to — `/checkout/return?orderId=…`.
 *
 * Both the success and failure redirects point here: which URI the browser arrived on
 * proves nothing, so this page asks our own API what the verified status is. Polling
 * also nudges the server to re-check with Keepz, which is why the flow completes even
 * when payment callbacks aren't enabled for our integrator.
 */
const CheckoutReturnPage = ({ isDark, setToast }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { socket } = useSocket();

  const orderId = searchParams.get("orderId");

  /**
   * Keepz redirects the frame it is running in, so after paying inside the checkout
   * modal this page loads *inside* that modal — a Mintly page nested in its own iframe.
   *
   * When that happens, hand off to the opener instead of rendering: the parent is the
   * same origin, so a postMessage reaches it, and CheckoutModal closes the frame and
   * takes over. Falls back to breaking out of the frame if no one is listening.
   */
  const isFramed = typeof window !== "undefined" && window.self !== window.top;

  useEffect(() => {
    if (!isFramed || !orderId) return undefined;

    // The parent answers with an ack. Without one we'd have to guess whether anybody
    // was listening — and guessing wrong navigates the buyer's whole tab out from
    // under an open modal, which is what used to happen here.
    let acknowledged = false;
    const onAck = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "keepz:checkout-ack" && event.data.orderId === orderId) {
        acknowledged = true;
        clearTimeout(escape);
      }
    };
    window.addEventListener("message", onAck);

    window.parent.postMessage(
      { type: "keepz:checkout-returned", orderId },
      window.location.origin
    );

    // Nobody answered, so this frame is orphaned — take over the tab rather than
    // leaving the buyer staring at a spinner inside a box.
    const escape = setTimeout(() => {
      if (acknowledged) return;
      try {
        window.top.location.href = `${window.location.origin}/checkout/return?orderId=${encodeURIComponent(orderId)}`;
      } catch {
        window.location.href = `/checkout/return?orderId=${encodeURIComponent(orderId)}`;
      }
    }, 4000);

    return () => {
      window.removeEventListener("message", onAck);
      clearTimeout(escape);
    };
  }, [isFramed, orderId]);

  const [phase, setPhase] = useState("checking"); // checking | paid | failed | slow
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const doneRef = useRef(false);
  const setToastRef = useRef(setToast);
  setToastRef.current = setToast;

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const ghostButton = "border-separator-default text-label-primary hover:bg-fill-quaternary";

  const stop = () => {
    doneRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const poll = useCallback(async () => {
    if (doneRef.current || !orderId) return;

    try {
      const data = await getOrderStatus(orderId);
      setOrder(data);

      if (data.paid) {
        stop();
        setPhase("paid");
        return;
      }
      if (data.settled) {
        stop();
        setPhase("failed");
        return;
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      // A 404 or 403 is terminal; anything else is worth another try.
      if ([403, 404].includes(err?.response?.status)) {
        stop();
        setError(message || "We couldn't find that order.");
        setPhase("failed");
        return;
      }
    }

    if (Date.now() - startedAtRef.current > CEILING_MS) {
      stop();
      setPhase("slow");
      return;
    }

    const step = POLL_STEPS[Math.min(attemptRef.current, POLL_STEPS.length - 1)];
    attemptRef.current += 1;
    timerRef.current = setTimeout(poll, step * 1000);
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setPhase("failed");
      setError("This link is missing an order reference.");
      return undefined;
    }
    poll();
    return stop;
  }, [orderId, poll]);

  // The socket usually beats the next poll, so the page flips the moment we fulfil.
  useEffect(() => {
    if (!socket || !orderId) return undefined;

    const onPayment = (payload) => {
      if (payload?.orderId !== orderId || doneRef.current) return;
      if (payload.status === "paid") {
        stop();
        setPhase("paid");
        // Pull the detail (product name, access window) for the confirmation.
        getOrderStatus(orderId).then(setOrder).catch(() => {});
      }
    };

    socket.on(SOCKET_KEEPZ_PAYMENT, onPayment);
    return () => socket.off(SOCKET_KEEPZ_PAYMENT, onPayment);
  }, [socket, orderId]);

  const goToPurchase = () => {
    const spaceId = order?.purchase?.mintSpaceId;
    navigate(spaceId ? `/mint-space/${spaceId}` : "/home");
  };

  /* ------------------------------------------------------------- rendering */

  if (isFramed) {
    return (
      <div className="flex items-center justify-center py-16">
        <FiLoader className="h-8 w-8 text-accent-text animate-spin" />
      </div>
    );
  }

  if (phase === "checking") {
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <FiLoader className="h-12 w-12 text-accent-text animate-spin mb-4" />
            <h2 className={`text-title-2 ${baseText}`}>
              Confirming your payment…
            </h2>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
              This usually takes a few seconds. You can leave this page open.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "paid") {
    const productName = order?.purchase?.productName;
    const accessUntil = order?.purchase?.accessUntil;
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="size-16 rounded-full bg-accent-default flex items-center justify-center mb-6">
              <FiCheck className="size-8 text-accent-on" />
            </div>
            <h2 className={`text-title-2 ${baseText}`}>Payment complete</h2>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
              {productName
                ? `You now have access to ${productName}.`
                : order?.type === "campaign_fund"
                ? "Your campaign budget has been topped up."
                : "Your balance has been updated."}
            </p>
            {order?.amount != null && (
              <p className={`text-subheadline-semibold ${baseText} mt-4`}>
                {order.amount} {order.currency}
              </p>
            )}
            {accessUntil && (
              <p className={`text-footnote ${mutedText} mt-1`}>
                Renews {new Date(accessUntil).toLocaleDateString()}
              </p>
            )}
            <button
              type="button"
              onClick={goToPurchase}
              className="mt-8 inline-flex h-button-m items-center rounded-radius-md bg-accent-default px-space-16 text-caps-button-m uppercase text-accent-on"
            >
              {order?.purchase ? "Open your Mint Space" : "Continue"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "slow") {
    return (
      <div className="flex w-full flex-col gap-space-24 font-ds">
        <div className="rounded-radius-lg bg-bg-raised p-space-24">
          <div className="flex flex-col items-center justify-center py-12">
            <FiClock className="h-12 w-12 text-accent-text mb-4" />
            <h2 className={`text-title-2 ${baseText}`}>
              Payment is still processing
            </h2>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
              Your bank is taking longer than usual. We'll notify you the moment it
              clears — you don't need to pay again.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                type="button"
                onClick={() => {
                  doneRef.current = false;
                  attemptRef.current = 0;
                  startedAtRef.current = Date.now();
                  setPhase("checking");
                  poll();
                }}
                className={`px-5 py-2.5 rounded-radius-sm border text-subheadline-semibold transition ${ghostButton}`}
              >
                Check again
              </button>
              <button
                type="button"
                onClick={() => navigate("/home")}
                className="px-5 py-2.5 rounded-radius-sm bg-accent-default text-accent-on text-subheadline-semibold"
              >
                Back to Mintly
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // failed / canceled / expired
  return (
    <div className="space-y-6">
      <div className={`rounded-radius-lg border p-8 backdrop-blur ${cardBase}`}>
        <div className="flex flex-col items-center justify-center py-12">
          <FiAlertCircle className="h-12 w-12 text-status-danger mb-4" />
          <h2 className={`text-title-2 ${baseText}`}>
            That payment didn't go through
          </h2>
          <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-md`}>
            {error || "You haven't been charged. You can try again whenever you're ready."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-radius-sm bg-accent-default text-accent-on text-subheadline-semibold"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate("/home")}
              className={`px-5 py-2.5 rounded-radius-sm border text-subheadline-semibold transition ${ghostButton}`}
            >
              Back to Mintly
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutReturnPage;
